import { useAuth } from "./useAuth";
import {
  Plan,
  PlanLimits,
  getPlanLimits,
  getRequiredPlan,
  PLAN_LABELS,
  PLAN_UPGRADE_ORDER,
} from "../config/plans.config";

export { PLAN_LABELS };
export type { Plan };

/** Where to send users who click "Upgrade" */
export const UPGRADE_URL = "/pricing";

export function usePlan() {
  const { user } = useAuth();
  // `plan` is embedded in the JWT and surfaced on the user object by useAuth.
  // It reflects the active workspace's plan (set server-side on login / workspace switch).
  const plan = ((user as any)?.plan || "free") as Plan;
  const limits = getPlanLimits(plan);

  /** Can the current user use this feature? */
  const canUse = (feature: keyof PlanLimits): boolean => {
    const val = limits[feature];
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val === -1 || val > 0;
    return false;
  };

  /** Raw limit value */
  const getLimit = (feature: keyof PlanLimits): number | boolean =>
    limits[feature];

  /** Is a numeric limit reached? (-1 = unlimited → never at limit) */
  const isAtLimit = (
    feature:
      | "maxDocuments"
      | "maxTeamMembers"
      | "maxAiScansPerMonth"
      | "maxAiChatsPerDay"
      | "maxCompanies",
    currentCount: number
  ): boolean => {
    const limit = limits[feature] as number;
    if (limit === -1) return false;
    return currentCount >= limit;
  };

  /** Which plan first unlocks this feature? */
  const requiredPlanFor = (feature: keyof PlanLimits): Plan =>
    getRequiredPlan(feature);

  const planIndex = PLAN_UPGRADE_ORDER.indexOf(plan);

  return {
    plan,
    planLabel: PLAN_LABELS[plan],
    limits,
    canUse,
    getLimit,
    isAtLimit,
    requiredPlanFor,
    isFreePlan: plan === "free",
    isProOrHigher: planIndex >= 1,
    isBusinessOrHigher: planIndex >= 2,
    isEnterprise: plan === "enterprise",
  };
}
